
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, Search, Eye } from 'lucide-react';
import { adminService, UserProfile } from '@/services/admin';
import { useToast } from '@/hooks/use-toast';

const Users = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<UserProfile | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const fetchUsers = async () => {
    try {
      const data = await adminService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: '無法載入會員列表',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (userId: string, updates: Partial<UserProfile>) => {
    try {
      await adminService.updateUser(userId, updates);
      await fetchUsers();
      toast({
        title: '更新成功',
        description: '會員資料已更新',
      });
      setIsEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        variant: 'destructive',
        title: '更新失敗',
        description: '無法更新會員資料',
      });
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (window.confirm(`確定要刪除會員 ${userEmail} 嗎？此操作無法復原。`)) {
      try {
        await adminService.deleteUser(userId);
        await fetchUsers();
        toast({
          title: '刪除成功',
          description: '會員帳號已刪除',
        });
      } catch (error) {
        console.error('Error deleting user:', error);
        toast({
          variant: 'destructive',
          title: '刪除失敗',
          description: '無法刪除會員帳號',
        });
      }
    }
  };

  const handleViewUser = (user: UserProfile) => {
    setViewingUser(user);
    setIsViewDialogOpen(true);
  };

  const handleEditUser = (user: UserProfile) => {
    setEditingUser({ ...user });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">會員管理</h1>
          <p className="text-gray-600 dark:text-gray-400">管理平台所有會員帳號</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>會員列表</CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="搜尋會員..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>暱稱</TableHead>
                <TableHead>真實姓名</TableHead>
                <TableHead>驗證狀態</TableHead>
                <TableHead>管理員</TableHead>
                <TableHead>積分</TableHead>
                <TableHead>註冊時間</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{user.nickname}</TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>
                    <Badge variant={user.is_verified ? "default" : "secondary"}>
                      {user.is_verified ? '已驗證' : '未驗證'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_admin ? "destructive" : "outline"}>
                      {user.is_admin ? '管理員' : '一般會員'}
                    </Badge>
                  </TableCell>
                  <TableCell>{user.points || 0}</TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('zh-TW')}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewUser(user)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditUser(user)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* 查看會員詳情對話框 */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>會員詳細資料</DialogTitle>
          </DialogHeader>
          {viewingUser && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Email</Label>
                <p className="text-sm">{viewingUser.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">暱稱</Label>
                <p className="text-sm">{viewingUser.nickname}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">真實姓名</Label>
                <p className="text-sm">{viewingUser.full_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">年齡</Label>
                <p className="text-sm">{viewingUser.age || '未設定'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">性別</Label>
                <p className="text-sm">{viewingUser.gender || '未設定'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">國家</Label>
                <p className="text-sm">{viewingUser.country || '未設定'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">積分</Label>
                <p className="text-sm">{viewingUser.points || 0}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">邀請碼</Label>
                <p className="text-sm">{viewingUser.invite_code || '未設定'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">驗證狀態</Label>
                <Badge variant={viewingUser.is_verified ? "default" : "secondary"}>
                  {viewingUser.is_verified ? '已驗證' : '未驗證'}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">管理員權限</Label>
                <Badge variant={viewingUser.is_admin ? "destructive" : "outline"}>
                  {viewingUser.is_admin ? '管理員' : '一般會員'}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">註冊時間</Label>
                <p className="text-sm">{new Date(viewingUser.created_at).toLocaleString('zh-TW')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 編輯會員對話框 */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>編輯會員資料</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">暱稱</Label>
                  <Input
                    value={editingUser.nickname}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, nickname: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">真實姓名</Label>
                  <Input
                    value={editingUser.full_name}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, full_name: e.target.value })
                    }
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">年齡</Label>
                  <Input
                    type="number"
                    value={editingUser.age || ''}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, age: e.target.value ? parseInt(e.target.value) : null })
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">性別</Label>
                  <Select 
                    value={editingUser.gender || ''} 
                    onValueChange={(value) => setEditingUser({ ...editingUser, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="選擇性別" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">男性</SelectItem>
                      <SelectItem value="female">女性</SelectItem>
                      <SelectItem value="other">其他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">國家</Label>
                  <Input
                    value={editingUser.country || ''}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, country: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">積分</Label>
                  <Input
                    type="number"
                    value={editingUser.points || 0}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, points: parseInt(e.target.value) || 0 })
                    }
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={editingUser.is_verified}
                    onCheckedChange={(checked) =>
                      setEditingUser({ ...editingUser, is_verified: checked })
                    }
                  />
                  <Label className="text-sm font-medium">已驗證</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={editingUser.is_admin}
                    onCheckedChange={(checked) =>
                      setEditingUser({ ...editingUser, is_admin: checked })
                    }
                  />
                  <Label className="text-sm font-medium">管理員權限</Label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingUser(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  onClick={() =>
                    handleUpdateUser(editingUser.id, {
                      nickname: editingUser.nickname,
                      full_name: editingUser.full_name,
                      age: editingUser.age,
                      gender: editingUser.gender,
                      country: editingUser.country,
                      points: editingUser.points,
                      is_verified: editingUser.is_verified,
                      is_admin: editingUser.is_admin,
                    })
                  }
                >
                  儲存
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
